import { Config } from '@src/core/Config';

export enum Bus {
  Announcer = 0,
  Sfx = 1,
  Music = 2,
}

function dbToGain(db: number): number {
  return Math.pow(10, db / 20);
}

/**
 * Master -> {announcer, sfx, music} gain buses. `duck()` implements the sidechain:
 * SFX -12 dB and Music -6 dB over a 20 ms attack while an announcer clip plays.
 */
export class SoundBusManager {
  readonly context: AudioContext;
  readonly master: GainNode;
  private readonly buses: GainNode[] = [];
  private readonly busLevels = [1, 0.8, 0.55];
  private duckReleaseAt = 0;

  constructor() {
    this.context = new AudioContext({ latencyHint: 'interactive' });
    this.master = this.context.createGain();
    this.master.gain.value = 0.9;
    this.master.connect(this.context.destination);
    for (let i = 0; i < 3; i++) {
      const g = this.context.createGain();
      g.gain.value = this.busLevels[i];
      g.connect(this.master);
      this.buses.push(g);
    }
  }

  bus(which: Bus): GainNode {
    return this.buses[which];
  }

  get now(): number {
    return this.context.currentTime;
  }

  async resume(): Promise<void> {
    if (this.context.state !== 'running') await this.context.resume();
  }

  /** Duck SFX and Music for `holdSeconds`, then release. Overlapping calls extend the hold. */
  duck(holdSeconds: number): void {
    const a = Config.audio;
    const t = this.context.currentTime;
    const attack = a.duckAttackMs / 1000;
    const release = a.duckReleaseMs / 1000;
    const releaseAt = Math.max(this.duckReleaseAt, t + holdSeconds);
    this.duckReleaseAt = releaseAt;

    this.schedule(this.buses[Bus.Sfx].gain, this.busLevels[Bus.Sfx], dbToGain(a.sfxDuckDb), t, attack, releaseAt, release);
    this.schedule(this.buses[Bus.Music].gain, this.busLevels[Bus.Music], dbToGain(a.musicDuckDb), t, attack, releaseAt, release);
  }

  private schedule(param: AudioParam, base: number, duckMul: number, t: number, attack: number, releaseAt: number, release: number): void {
    param.cancelScheduledValues(t);
    param.setValueAtTime(param.value, t);
    param.linearRampToValueAtTime(base * duckMul, t + attack);
    param.setValueAtTime(base * duckMul, releaseAt);
    param.linearRampToValueAtTime(base, releaseAt + release);
  }

  setBusLevel(which: Bus, level: number): void {
    this.busLevels[which] = level;
    const g = this.buses[which].gain;
    g.cancelScheduledValues(this.context.currentTime);
    g.setTargetAtTime(level, this.context.currentTime, 0.05);
  }

  destroy(): void {
    void this.context.close();
  }
}
